// @ts-check
import {useInfiniteQuery} from "@tanstack/react-query"

import {
  isPaginationByPage,
  isPaginationByOffset,
  isUnifiedListResponse,
  PaginationQueryContext,
} from "../../core"

/**
 * @template TResult
 * @param {TResult} lastPageResult
 * @param {TResult[]} allPagesResult
 * @returns {PaginationQueryContext|undefined}
 */
const unifiedGetNextPageParam = (lastPageResult, allPagesResult) => {
  if (!isUnifiedListResponse(lastPageResult)) return undefined
  const pagination = lastPageResult.meta.pagination
  if (isPaginationByPage(pagination)) {
    const currentPage = pagination.page
    const totalPages = pagination.pageCount
    if (currentPage >= totalPages) return undefined
    return {
      page: currentPage + 1,
      pageSize: pagination.pageSize,
    }
  } else if (isPaginationByOffset(pagination)) {
    if (pagination.start + pagination.limit >= pagination.total)
      return undefined
    return {
      start: pagination.start + pagination.limit,
      limit: pagination.limit,
    }
  }
  return undefined
}

/**
 * @template TItem
 * @template {import('../../core').UnifiedListResponse<TItem>} TQueryFnData
 * @template {Error} TError
 * @template {import('@tanstack/react-query').QueryKey} TQueryKey
 * @template [TData=TQueryFnData]
 * @param {TQueryKey} queryKey
 * @param {import('@tanstack/react-query').QueryFunction<TQueryFnData, TQueryKey>} queryFn
 * @param {import('@tanstack/react-query').UseInfiniteQueryOptions<TQueryFnData,TError,TData,TQueryFnData,TQueryKey>=} options
 */
export function useUnifiedInfiniteQuery(queryKey, queryFn, options = {}) {
  const {onError, getNextPageParam, ...queryOptions} = options

  return useInfiniteQuery(queryKey, queryFn, {
    ...queryOptions,
    getNextPageParam: getNextPageParam ?? unifiedGetNextPageParam,
  })
}
